import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';

/**
 * useSessionState — a drop-in replacement for useState that persists
 * the value in sessionStorage so it survives in-app navigation
 * (e.g. opening an action item detail and coming back to the board).
 *
 * The value is stored as JSON under the given key.
 * On first render the hook reads sessionStorage; if no stored value
 * exists it falls back to `defaultValue`.
 *
 * @template T
 * @param {string}  key           sessionStorage key
 * @param {T}       defaultValue  value used when nothing is stored yet
 * @returns {[T, (value: T | ((prev: T) => T)) => void]}
 */
const useSessionState = (key, defaultValue) => {
    const [value, setValue] = useState(() => {
        try {
            const stored = sessionStorage.getItem(key);
            if (stored !== null) {
                return JSON.parse(stored);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            // Corrupted or non-JSON value — fall through to default
        }
        return defaultValue;
    });

    const setPersistedValue = useCallback((newValue) => {
        setValue(prev => {
            const resolved = typeof newValue === 'function' ? newValue(prev) : newValue;
            try {
                sessionStorage.setItem(key, JSON.stringify(resolved));
            } catch (catchError) {
                Sentry.captureException(catchError);
                // Storage full or unavailable — silently degrade
            }
            return resolved;
        });
    }, [key]);

    return [value, setPersistedValue];
};

export default useSessionState;
